import isEmpty from 'lodash/isEmpty';
import { generatePath, matchRoutes } from 'react-router-dom';

import { isOneClick, organizationQueryKey } from '@cast/constants';

import { PRODUCT_BASE_PATHS } from '@cast/console-constants';
import { LIST_PRICE_QUERY_PARAM } from 'features/cost-report/constants';

import { makeSearchQuery } from '@cast/shared-search-ui';

// prettier-ignore
const availabilityMatrix = [
  /*                      cluster | workload  | workloads | namespace | namespaces | allocation groups | allocation group */
  /* compute cost */    [ true, true, true, true, true, true , true],
  /* reliability */     [ true, true, true, false, false, false, false],
  /* network cost */    [ true, true, true, false, true, true, false],
  /* efficiency cost */ [ true, true, true, false, false, true, false],
  /* gpu utilization */ [ false, true, true, false, false, false, false],
];

const BASE_URL = '/external-clusters/:clusterId/cost-report';
const BASE_URL_OP = `${PRODUCT_BASE_PATHS.automation}/external-clusters/:clusterId/cost-report`;
const PERSISTENT_QUERY_PARAMS = [
  organizationQueryKey,
  'cost_report_date_preset',
  'cost_report_from',
  'cost_report_to',
  'cost_report_timezone',
  LIST_PRICE_QUERY_PARAM,
];

const customRouteSegments = {
  comparison: 'comparison',
  savingsReport: 'savings-report',
} as const;

// Note: order is important when matching routes and checking availability by index, make sure "details" segments are defined before "list" segments
const routeSegmentsByObject = {
  cluster: 'cluster',
  workload: 'workloads/__report_type__/:workloadName/:namespace/:workloadType',
  workloads: 'workloads',
  namespace: 'namespaces/__report_type__/:namespace',
  namespaces: 'namespaces',
  allocationGroups: 'allocation-groups',
  allocationGroup: 'allocation-groups/__report_type__/:allocationGroup',
} as const;

// Note: order is important when checking availability by index
const routeSegmentsByReportingType = {
  computeCost: 'compute-cost',
  reliability: 'reliability',
  networkCost: 'network-cost',
  efficiency: 'efficiency',
  gpuUtilization: 'gpu-utilization',
} as const;

type CustomRouteSegmentKeys = keyof typeof customRouteSegments;
export type ObjectSegmentKeys = keyof typeof routeSegmentsByObject;
export type ReportTypeSegmentKeys = keyof typeof routeSegmentsByReportingType;

const INVENTORY_HOSTED_OBJECTS = new Set<ObjectSegmentKeys>([
  'namespace',
  'namespaces',
  'allocationGroups',
  'allocationGroup',
]);

const isInventoryHostedObject = (object: ObjectSegmentKeys) =>
  INVENTORY_HOSTED_OBJECTS.has(object);

const applyInventoryMountToPath = (path: string) =>
  path.replace('/cost-report/', '/inventory/');

export type CostMonitoringRouteKeys =
  | ObjectSegmentKeys
  | CustomRouteSegmentKeys
  | `${ObjectSegmentKeys}.${ReportTypeSegmentKeys}`;

type NavigationParams = {
  object?: ObjectSegmentKeys;
  reportType?: ReportTypeSegmentKeys;
};

type RoutePath = string;

type RouteMatch = {
  object: ObjectSegmentKeys;
  reportType: ReportTypeSegmentKeys;
  path: RoutePath;
  params: Record<string, string>;
};

export type ClusterId = 'clusterId';
export type WorkloadName = 'workloadName';
export type Namespace = 'namespace';
export type WorkloadType = 'workloadType';
export type AllocationGroup = 'allocationGroup';

export type LinkParams<T extends string> = Partial<Record<T, string>>;
export type LinkFilterParams<T extends string> = Partial<Record<T, string>>;

const isAvailableRoute = (o: ObjectSegmentKeys, t: ReportTypeSegmentKeys) => {
  return availabilityMatrix[
    Object.keys(routeSegmentsByReportingType).indexOf(t)
  ][Object.keys(routeSegmentsByObject).indexOf(o)];
};

// each route might have two urls, regular and for an opinionated platform (OP)
type RouteUrls = [url: string, urlOp: string];

const COST_MONITORING_PATHS = ((): Record<
  CostMonitoringRouteKeys,
  RouteUrls
> => {
  const r = {} as Record<CostMonitoringRouteKeys, RouteUrls>;

  function toRouteUrls(pathSegment: string): RouteUrls {
    return [`${BASE_URL}/${pathSegment}`, `${BASE_URL_OP}/${pathSegment}`];
  }

  (Object.keys(customRouteSegments) as CustomRouteSegmentKeys[]).forEach(
    (object) => {
      r[object] = toRouteUrls(customRouteSegments[object]);
    }
  );

  // prettier-ignore
  (Object.keys(routeSegmentsByObject) as ObjectSegmentKeys[]).forEach(
    (object) => {
      const objectSegment = routeSegmentsByObject[object];
      if (!objectSegment.includes('__report_type__')) {
        r[object] = toRouteUrls(routeSegmentsByObject[object]);
      }

      (
        Object.keys(routeSegmentsByReportingType) as ReportTypeSegmentKeys[]
      ).forEach((reportingType) => {
        if (isAvailableRoute(object, reportingType)) {
          const key: CostMonitoringRouteKeys = `${object}.${reportingType}`;
          const reportingTypeSegment =
            routeSegmentsByReportingType[reportingType];
          if (objectSegment.includes('__report_type__')) {
            r[key] = toRouteUrls(routeSegmentsByObject[object].replace(
              '__report_type__',
              reportingTypeSegment
            ));
          } else {
            r[key] = toRouteUrls(`${routeSegmentsByObject[object]}/${routeSegmentsByReportingType[reportingType]}`)
          }
        }
      });
    }
  );

  return r;
})();

export const getRoutePath = (key: CostMonitoringRouteKeys): string => {
  if (!(key in COST_MONITORING_PATHS)) {
    return '/404';
  }

  // always return regular url
  return COST_MONITORING_PATHS[key][0];
};

/**
 * OneClick remounts namespaces / allocation groups under `/inventory/`.
 * Normalize to `/cost-report/` so shared route matching still resolves object context.
 */
const normalizePathnameForMatching = (pathname: string) =>
  pathname.replace(
    /(\/external-clusters\/[^/]+)\/inventory\//,
    '$1/cost-report/'
  );

const retrieveCurrentRouteContext = (): RouteMatch | undefined => {
  const pathname = normalizePathnameForMatching(window.location.pathname);

  return (
    Object.keys(COST_MONITORING_PATHS) as CostMonitoringRouteKeys[]
  ).reduce((acc, k) => {
    // match regular or OP route
    const match = matchRoutes(
      COST_MONITORING_PATHS[k].map((path) => ({ path })),
      pathname
    );

    if (match) {
      const [object, reportType] = k.split('.') as [
        ObjectSegmentKeys,
        ReportTypeSegmentKeys
      ];

      return {
        object,
        reportType,
        path: COST_MONITORING_PATHS[k][0],
        params: match[0].params,
      } as RouteMatch;
    }

    return acc;
  }, undefined as RouteMatch | undefined);
};

const mergeQueryParams = (
  q1: URLSearchParams,
  q2: URLSearchParams
): URLSearchParams => {
  const mergerParams = new URLSearchParams();
  q1.forEach((value, key) => {
    mergerParams.append(key, value);
  });

  q2.forEach((value, key) => {
    mergerParams.append(key, value);
  });

  return mergerParams;
};
const appendQueryParams = (path: string, queryParameters: URLSearchParams) => {
  const mergerParams = new URLSearchParams();
  queryParameters.forEach((value, key) => {
    mergerParams.append(key, value);
  });

  const searchParams = new URLSearchParams(window.location.search);
  // Copy query parameters from the current URL
  searchParams.forEach((value, key) => {
    if (PERSISTENT_QUERY_PARAMS.includes(key)) {
      mergerParams.append(key, value);
    }
  });

  return `${path}?${mergerParams.toString()}`;
};

export const buildFilterParams = (
  id: string,
  queryParams: URLSearchParams = new URLSearchParams(),
  filterParams: LinkFilterParams<string>
) => {
  if (filterParams && !isEmpty(filterParams)) {
    return mergeQueryParams(
      queryParams,
      makeSearchQuery({
        id,
        ...filterParams,
      })
    );
  }
  return queryParams;
};

export const buildLinkToReport = (
  destinationPath: string,
  params: Record<string, string> = {},
  queryParams: URLSearchParams = new URLSearchParams()
) => {
  // clusterId is a required param for all routes
  const baseMatch = matchRoutes(
    [
      { path: `/external-clusters/:clusterId/*` },
      {
        path: `${PRODUCT_BASE_PATHS.automation}/external-clusters/:clusterId/*`,
      },
    ],
    window.location.pathname
  );

  // retrieve even more data from the current URL
  const match = retrieveCurrentRouteContext();
  const mergedParams: Record<string, string | undefined> = {
    ...baseMatch?.[0]?.params,
    ...match?.params,
    ...params,
  };

  if (isEmpty(mergedParams)) {
    throw new Error(`Missing params for route ${destinationPath}`);
  }

  const path = generatePath(destinationPath, mergedParams);

  // Validate whether all params are provided
  const missingParams = path.match(/\/:\w+/g);
  if (missingParams?.length) {
    throw new Error(
      `Missing params ${missingParams
        .join(', ')
        .replaceAll('/:', '')} for route ${path}`
    );
  }

  return appendQueryParams(path, queryParams);
};

/**
 * Returns either regular url or OP url for a given routing key.
 */
export function getDynamicRoutePath(key: CostMonitoringRouteKeys): string {
  const paths = COST_MONITORING_PATHS[key];
  return paths[1];
}

export const getLinkToReport = (
  { object, reportType }: NavigationParams,
  params: Record<string, string> = {},
  queryParams: URLSearchParams = new URLSearchParams()
) => {
  // retrieve even more data from the current URL
  const match = retrieveCurrentRouteContext();

  // building navigation key by importance weight: given params => existing context => default
  const _object: ObjectSegmentKeys = object ?? match?.object ?? 'cluster';
  const _reportType: ReportTypeSegmentKeys =
    reportType ?? match?.reportType ?? 'computeCost';

  const key: CostMonitoringRouteKeys = isAvailableRoute(_object, _reportType)
    ? `${_object}.${_reportType}`
    : `${_object}.computeCost`;

  let destinationPath = getDynamicRoutePath(key);

  if (isOneClick() && isInventoryHostedObject(_object)) {
    destinationPath = applyInventoryMountToPath(destinationPath);
  }

  return buildLinkToReport(destinationPath, params, queryParams);
};
